import {useContext} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useShallow} from 'zustand/react/shallow';
import {AxiosError} from 'axios';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {MessageThreadSummary} from '@/api/dto/message';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import {useMessageStore} from '@/store/message_store';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';
import {log} from '@/helpers/logger';

export function useMessageThreadItem() {
  const { carecoApi } = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const addAlert = useAlertStore((state) => state.addAlert);

  const [isApiLoading, setApiLoading] = useMessageStore(useShallow((state) => [
    state.isApiLoading,
    state.setApiLoading,
  ]));

  const readMessageThread = (messageThreadSummary: MessageThreadSummary, read: boolean) => {
    setApiLoading(true);
    carecoApi?.readMessageThread(messageThreadSummary.id, read)
      .then(() => {
        addAlert({
          type: ToastType.SUCCESS,
          content: read ? 'Message marked as read.' : 'Message marked as unread.',
        });
        void queryClient.invalidateQueries({ queryKey: ['messageThreadInbox'], exact: false });
        void queryClient.invalidateQueries({ queryKey: ['messageThreadSummaries', messageThreadSummary.clientId] });
      })
      .catch((error: AxiosError) => {
        addAlert({
          type: ToastType.ERROR,
          content: `Failed to mark a message as read. ${GENERICERROR} ${Helpers.traceId(error)}`,
        });
        log('error', 'Error updating message as read', error);
      })
      .finally(() => {
        setApiLoading(false);
      });
  };

  return {
    isApiLoading,
    readMessageThread,
  };
}
